import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlassCard, Button, Icon, StatusPill, SearchInput, FilterSelect, Pagination } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useApiData } from '../data/useApiData';
import { useAllVendors } from '../data/useAllVendors';
import { useVendorState, useRegistryStatus } from '../data/useVendorState';
import { useShellToast } from '../useShellToast';
import { useAuth } from '../../../../contexts/AuthContext';
import { selfRegularizationService, surepassUsageService, getErrorMessage } from '../../../../services/apiService';
import { CHECKS, checkByKey, formatInr, NEEDS_FILTERS, matchesNeed, toneFor, perVendorTotal, describeSheet } from '../data/selfRegularization';
import { VENDOR_STATUS_PILL, vendorPill } from '../data/messageStatus';
import { RegistryNotice } from '../components/SelfRegularizationBits';
import { VendorSheetUpload } from '../components/VendorSheetUpload';

/**
 * New verification run — three steps, the way the campaign wizard splits its work:
 *
 *   1. Checks   — what to verify, each priced per vendor.
 *   2. Vendors  — who, from the real vendor base; the cost multiplies as rows are ticked.
 *   3. Review   — what will be charged, stated plainly, and the admin's tick before it starts.
 *
 * The estimate rail on the right is the point of the screen: ₹2 × 10 vendors reads "₹20" before
 * a single call is made, and a vendor whose record cannot support a check is skipped, not billed.
 *
 * The vendors step holds the whole base in the browser, so "everyone whose GST is not yet
 * verified" is one filter, "select all" is instant, and a sheet of vendor codes ticks its rows.
 */

const STEPS = [
  { n: '01', key: 'checks', label: 'Checks' },
  { n: '02', key: 'vendors', label: 'Vendors' },
  { n: '03', key: 'review', label: 'Review & start' },
];
const PAGE_SIZE = 10;
const EMPTY_FILTERS = { status: 'All', msme: 'All', category: 'All', type: 'All', country: 'All', region: 'All' };
const MSME_LABEL = { MSME: 'MSME', NON_MSME: 'Non-MSME', PENDING: 'Pending' };
const FILTER_FIELDS = [
  { key: 'status', label: 'Status', icon: 'verified_user', field: 'status' },
  { key: 'msme', label: 'MSME', icon: 'storefront', field: 'msme_status' },
  { key: 'category', label: 'Category', icon: 'category', field: 'supplier_category' },
  { key: 'type', label: 'Type', icon: 'factory', field: 'vendor_type' },
  { key: 'country', label: 'Country', icon: 'public', field: 'country_origin' },
  { key: 'region', label: 'Region', icon: 'map', field: 'region' },
];

const FIELD =
  'w-full rounded-xl border border-cos-line bg-white/70 px-3 py-2.5 text-sm text-cos-ink placeholder:text-cos-ink-secondary/60 focus:border-cos-teal/50 focus:outline-none focus:ring-2 focus:ring-cos-teal/15 transition';
const LABEL = 'mb-1.5 block text-[13px] font-semibold text-cos-ink';
const CHECKBOX = 'h-4 w-4 cursor-pointer rounded border-cos-line accent-[#0A9188]';
const num = (n) => Number(n || 0).toLocaleString('en-IN');

function humanise(key, value) {
  if (value === 'All') return 'All';
  if (key === 'msme') return MSME_LABEL[value] || value;
  if (key === 'status') return VENDOR_STATUS_PILL[value]?.label || value;
  return value;
}

function PageCheckbox({ all, some, onChange, disabled }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !all && some;
  }, [all, some]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={CHECKBOX}
      checked={all}
      disabled={disabled}
      onChange={onChange}
      aria-label={all ? 'Untick every vendor on this page' : 'Tick every vendor on this page'}
    />
  );
}

/** One check, as a card the operator ticks. */
function CheckCard({ check, selected, onToggle }) {
  const disabled = !check.available;
  const tone = toneFor(check.key);
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={selected}
      className={[
        'group relative flex w-full flex-col gap-3 rounded-2xl border p-4 text-left transition-all duration-200',
        disabled
          ? 'cursor-not-allowed border-cos-line bg-white/30 opacity-60'
          : selected
            ? `bg-white ${tone.ring}`
            : 'border-cos-line bg-white/55 hover:border-cos-teal/50 hover:bg-white/80',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${selected ? tone.solid : tone.icon}`}>
            <Icon name={check.icon} className="text-[22px]" weight={500} />
          </span>
          <div>
            <div className="text-[15px] font-bold tracking-tight text-cos-ink">{check.label}</div>
            <div className="mono text-[12px] text-cos-ink-secondary">
              {check.local ? (
                <>
                  Free <span className="text-cos-ink-muted">· from the GSTIN, no call</span>
                </>
              ) : (
                <>
                  {formatInr(check.price_inr)} <span className="text-cos-ink-muted">per vendor</span>
                </>
              )}
            </div>
          </div>
        </div>
        <span
          className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border transition ${
            selected ? `${tone.solid} border-transparent` : 'border-cos-line bg-white text-transparent group-hover:border-cos-teal/60'
          }`}
          aria-hidden="true"
        >
          <Icon name="check" className="text-[16px]" weight={700} />
        </span>
      </div>
      <p className="text-[12px] leading-[18px] text-cos-ink-secondary">{check.description}</p>
      <div className="flex flex-col gap-1.5 text-[12px]">
        <div className="flex items-center gap-1.5 text-cos-ink-secondary">
          <Icon name="input" className="text-[15px] text-cos-ink-muted" />
          Needs the <span className="font-semibold text-cos-ink">{check.needsLabel}</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {check.facts.map((f) => (
            <span key={f} className="rounded-md bg-[rgba(70,80,72,0.06)] px-1.5 py-0.5 text-2xs font-medium text-cos-ink-secondary">
              {f}
            </span>
          ))}
        </div>
      </div>
      {disabled && (
        <p className="flex items-center gap-1 text-[12px] text-cos-status-amber">
          <Icon name="block" className="text-[14px]" />
          {check.unavailable_reason || 'Not enabled for this account yet'}
        </p>
      )}
    </button>
  );
}

/** The right-hand rail: what this run will cost, live. */
function EstimateRail({ checks, vendorCount, estimate, step, catalogue }) {
  const selected = catalogue.filter((c) => checks.has(c.key));
  const perCheck = new Map((estimate?.per_check || []).map((c) => [c.key, c]));
  // Until a vendor is ticked the rail prices one vendor — "₹11 per vendor" from the first step —
  // and only then hands over to the server's estimate, which multiplies and knows who is skipped.
  const perVendor = vendorCount === 0;
  const cost = perVendor ? perVendorTotal(checks, catalogue) : estimate?.cost_inr ?? 0;
  const calls = estimate?.calls ?? 0;
  return (
    <GlassCard className="xl:sticky xl:top-4 xl:self-start">
      <div className="eyebrow mb-2">Estimate</div>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <div className="mono text-[34px] font-bold leading-none tracking-tight text-cos-ink">{formatInr(cost)}</div>
        {perVendor && selected.length > 0 && <span className="text-[13px] font-semibold text-cos-ink-secondary">per vendor</span>}
      </div>
      <p className="mt-1.5 text-[12px] text-cos-ink-secondary">
        {vendorCount === 0
          ? selected.length === 0
            ? 'Pick at least one check, then the vendors.'
            : step === 0
              ? 'Next, pick the vendors — this multiplies by every vendor you tick.'
              : 'Tick vendors — this multiplies by every vendor you tick.'
          : (
            <>
              <span className="mono font-semibold text-cos-ink">{num(vendorCount)}</span> vendor{vendorCount === 1 ? '' : 's'} ×{' '}
              <span className="mono font-semibold text-cos-ink">{selected.length}</span> check{selected.length === 1 ? '' : 's'} ={' '}
              <span className="mono font-semibold text-cos-ink">{num(calls)}</span> registry call{calls === 1 ? '' : 's'}
            </>
          )}
      </p>

      {selected.length > 0 && (
        <div className="mt-4 flex flex-col divide-y divide-cos-line-subtle border-t border-cos-line-subtle">
          {selected.map((c) => {
            const e = perCheck.get(c.key);
            return (
              <div key={c.key} className="py-2.5">
                <div className="flex items-center justify-between gap-3 text-[13px]">
                  <span className="flex items-center gap-1.5 font-medium text-cos-ink">
                    <Icon name={c.icon} className={`text-[16px] ${toneFor(c.key).text}`} />
                    {c.label}
                  </span>
                  <span className="mono text-cos-ink">{c.local ? 'Free' : perVendor ? formatInr(c.price_inr) : e ? formatInr(e.cost_inr) : '…'}</span>
                </div>
                <div className="mt-0.5 flex items-center justify-between text-[11px] text-cos-ink-muted">
                  <span>
                    {c.local
                      ? perVendor || !e
                        ? 'from the GSTIN · no call'
                        : `${num(e.runnable)} from the GSTIN · no call`
                      : perVendor
                        ? 'per vendor'
                        : e
                          ? `${num(e.runnable)} × ${formatInr(e.price_inr ?? c.price_inr)}`
                          : 'Estimating…'}
                  </span>
                  {e && e.skipped > 0 && (
                    <span className="text-cos-status-amber" title={e.skipped_reason}>
                      {num(e.skipped)} skipped · no {c.needsLabel}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 border-t border-cos-line-subtle pt-3 text-[12px] leading-[17px] text-cos-ink-secondary">
        <p className="flex items-start gap-1.5">
          <Icon name="unsubscribe" className="mt-px shrink-0 text-[15px] text-cos-teal-deep" />
          No email goes to any vendor.
        </p>
        <p className="flex items-start gap-1.5">
          <Icon name="payments" className="mt-px shrink-0 text-[15px] text-cos-teal-deep" />
          Nothing is charged until you start. A vendor whose record cannot support a check is skipped, not billed.
        </p>
      </div>
    </GlassCard>
  );
}

/** "✓ GST · PAN" — what earlier approved runs (or onboarding) already confirmed for a vendor. */
function VerifiedChips({ verified }) {
  const keys = CHECKS.filter((c) => verified.has(c.key));
  if (keys.length === 0) return <span className="text-2xs text-cos-ink-muted">Never</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {keys.map((c) => (
        <span key={c.key} className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-2xs font-semibold ${toneFor(c.key).bg} ${toneFor(c.key).text}`} title={`${c.label} verified`}>
          <Icon name="verified" className="text-[12px]" weight={600} />
          {c.label}
        </span>
      ))}
    </div>
  );
}

export function SelfRegularizationWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useShellToast();
  const { isAdmin } = useAuth();
  // Arriving from a suggestion or a run: the check, the filter, sometimes the vendors, are already known.
  const hasPrefill = searchParams.has('checks') || searchParams.has('vendors');

  const [step, setStep] = useState(0);
  const [checks, setChecks] = useState(() => new Set());
  const [name, setName] = useState('');
  const [ack, setAck] = useState(false);
  const [starting, setStarting] = useState(false);

  // ---- Vendors: the whole base in the browser, filtered here, selection as a map of rows --------
  const all = useAllVendors({ enabled: step >= 1 || hasPrefill });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [need, setNeed] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Map());
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [sheet, setSheet] = useState(null);

  const base = useMemo(() => all.vendors || [], [all.vendors]);
  // What each record can supply and what is already verified — the needs filter and the table read these.
  const registry = useRegistryStatus();
  const vendorState = useVendorState({ enabled: step >= 1 || hasPrefill });
  const state = vendorState.byId;
  // The catalogue with the server's own prices and availability laid over it.
  const catalogue = useMemo(() => {
    const byKey = new Map((registry.checks || []).map((c) => [c.key, c]));
    return CHECKS.map((c) => ({ ...c, ...(byKey.get(c.key) || {}) }));
  }, [registry.checks]);

  const optionValues = (f) => {
    const values = [...new Set(base.map((v) => v[f.field]).filter((x) => x !== null && x !== undefined && x !== ''))].sort();
    const current = filters[f.key];
    if (current !== 'All' && !values.includes(current)) values.unshift(current);
    return values;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return base.filter((v) => {
      for (const f of FILTER_FIELDS) if (filters[f.key] !== 'All' && v[f.field] !== filters[f.key]) return false;
      if (need !== 'all' && !matchesNeed(need, state.get(v.id))) return false;
      if (!q) return true;
      return [v.company_name, v.vendor_code, v.email].some((s) => String(s || '').toLowerCase().includes(q));
    });
  }, [base, filters, need, search, state]);

  const filteredIds = useMemo(() => new Set(filtered.map((v) => v.id)), [filtered]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const vendors = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const bump = () => setSelectionVersion((v) => v + 1);
  const toggle = (v) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(v.id)) next.delete(v.id);
      else next.set(v.id, v);
      return next;
    });
    bump();
  };
  const pageAll = vendors.length > 0 && vendors.every((v) => selected.has(v.id));
  const pageSome = vendors.some((v) => selected.has(v.id));
  const togglePage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (pageAll) vendors.forEach((v) => next.delete(v.id));
      else vendors.forEach((v) => next.set(v.id, v));
      return next;
    });
    bump();
  };
  const clearSelection = () => {
    setSelected(new Map());
    bump();
  };
  const selectAllMatching = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      filtered.forEach((v) => next.set(v.id, v));
      return next;
    });
    bump();
  };
  const allMatchingSelected = filtered.length > 0 && filtered.every((v) => selected.has(v.id));

  // From a suggestion ("run GST on everyone not yet verified") or a run ("re-check these"): set the
  // check and the filter at once; with auto=1, or named vendors, tick them and go straight to the review.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!hasPrefill || prefilled.current) return;
    const checksParam = (searchParams.get('checks') || '').split(',').filter((k) => checkByKey(k));
    const needParam = searchParams.get('need');
    if (checksParam.length && checks.size === 0) setChecks(new Set(checksParam));
    if (needParam && need === 'all' && NEEDS_FILTERS.some((f) => f.key === needParam)) setNeed(needParam);
    if (!all.vendors) return;
    prefilled.current = true;
    const ids = (searchParams.get('vendors') || '').split(',').map(Number).filter((n) => n > 0);
    if (ids.length) {
      const wanted = new Set(ids);
      setSelected(new Map(all.vendors.filter((v) => wanted.has(v.id)).map((v) => [v.id, v])));
      bump();
      setStep(checksParam.length ? 2 : 1);
      return;
    }
    if (searchParams.get('auto') === '1' && checksParam.length) {
      setSelected(new Map(filtered.map((v) => [v.id, v])));
      bump();
      setStep(2);
    } else {
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all.vendors, filtered]);

  /**
   * A sheet of vendor codes ticks the vendors it names; unknown codes are reported, not guessed.
   * The summary is a toast now and a line under the upload button until the next sheet.
   */
  const onSheet = (res) => {
    const byId = new Map(base.map((v) => [v.id, v]));
    setSelected((prev) => {
      const next = new Map(prev);
      for (const m of res.matched) next.set(m.id, byId.get(m.id) || m);
      return next;
    });
    bump();
    setSheet(res);
    toast(describeSheet(res, { maxCodes: 5 }), { tone: res.matched.length === 0 ? 'error' : res.missing.length ? 'warning' : 'success' });
  };
  // Matched rows the current filters hide: ticked all the same, but the operator should know.
  const sheetHidden = sheet ? sheet.matched.filter((m) => !filteredIds.has(m.id)).length : 0;

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };
  const activeFilters = Object.values(filters).filter((v) => v !== 'All').length + (need !== 'all' ? 1 : 0);
  const isFiltered = activeFilters > 0 || search.trim() !== '';
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setNeed('all');
    setSearch('');
    setPage(1);
  };

  // ---- The estimate, recomputed whenever the checks or the selection change ---------------------
  const checkKeys = useMemo(() => CHECKS.filter((c) => checks.has(c.key)).map((c) => c.key), [checks]);
  const canEstimate = checkKeys.length > 0 && selected.size > 0;
  const est = useApiData(
    () => selfRegularizationService.estimate({ checks: checkKeys, vendor_ids: Array.from(selected.keys()) }),
    [checkKeys.join(','), selectionVersion],
    { enabled: canEstimate }
  );
  const estimate = canEstimate ? est.data?.data || null : null;

  // This month's real provider usage sits beside the estimate on the review step.
  const usage = useApiData(() => surepassUsageService.summary(), [], { enabled: step === 2 });

  const toggleCheck = (key) => {
    setChecks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canContinue = (step === 0 && checkKeys.length > 0) || (step === 1 && selected.size > 0);
  const canStart = step === 2 && isAdmin && ack && checkKeys.length > 0 && selected.size > 0 && !starting && !!estimate && !est.loading && !registry.providerProblem;

  const cancel = () => navigate('/campaigning-tool/regularization/self');

  usePageChrome(
    () => ({
      title: 'New verification run',
      subtitle: ['Choose what to verify', 'Choose the vendors', 'Check the cost, then start'][step],
      actions: (
        <Button variant="ghost" icon="close" onClick={cancel}>
          Cancel
        </Button>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step]
  );

  const start = async () => {
    if (!canStart) return;
    setStarting(true);
    try {
      const res = await selfRegularizationService.createRun({
        name,
        checks: checkKeys,
        vendor_ids: Array.from(selected.keys()),
      });
      const run = res.data;
      toast(`Verifying ${num(run.vendor_count)} vendor${run.vendor_count === 1 ? '' : 's'} — nothing was sent to anyone`, { tone: 'success' });
      navigate(`/campaigning-tool/regularization/self/runs/${run.id}`);
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not start the run', { tone: 'error' });
      setStarting(false);
    }
  };

  const selectedCount = selected.size;
  const skippedTotal = (estimate?.per_check || []).reduce((n, c) => n + c.skipped, 0);
  const loadingList = (all.loading && !all.vendors) || vendorState.loading;
  const needLabel = NEEDS_FILTERS.find((f) => f.key === need)?.label || 'All vendors';

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="flex min-w-0 flex-col gap-4">
        {/* stepper — the campaign wizard's, so the two feel like one tool */}
        <div className="flex flex-wrap items-center gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div
                className={[
                  'flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold',
                  i < step
                    ? 'border-cos-teal/30 bg-cos-teal-faint/50 text-cos-teal-deep'
                    : i === step
                      ? 'border-cos-teal bg-white text-cos-ink shadow-cos-teal-glow'
                      : 'border-cos-line bg-white/40 text-cos-ink-secondary',
                ].join(' ')}
              >
                {i < step ? <Icon name="check" className="text-[17px]" /> : <span className="mono text-[12px]">{s.n}</span>}
                <span>{s.label}</span>
              </div>
              {i < STEPS.length - 1 ? <Icon name="chevron_right" className="text-[18px] text-cos-ink-muted" /> : null}
            </React.Fragment>
          ))}
        </div>

        <RegistryNotice status={registry} />

        {/* ------------------------------------------------------------------ 1 checks */}
        {step === 0 && (
          <GlassCard>
            <div className="mb-4">
              <h3 className="text-[15px] font-bold tracking-tight text-cos-ink">What should the registries confirm?</h3>
              <p className="mt-0.5 text-[12px] text-cos-ink-secondary">
                Each check reads the value already on the vendor's record and asks the registry about it. Pick one or several.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {catalogue.map((c) => (
                <CheckCard key={c.key} check={c} selected={checks.has(c.key)} onToggle={() => toggleCheck(c.key)} />
              ))}
            </div>
          </GlassCard>
        )}

        {/* ------------------------------------------------------------------ 2 vendors */}
        {step === 1 && (
          <GlassCard padded={false}>
            <div className="flex flex-col gap-3 px-5 pt-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-[15px] font-bold tracking-tight text-cos-ink">Which vendors?</h3>
                  <p className="mt-0.5 text-[12px] text-cos-ink-secondary">
                    Filter to the ones that need it — say, everyone whose GST is not yet verified — then tick rows, take everyone that matches, or upload a sheet of vendor codes.
                  </p>
                </div>
                <div className="flex max-w-full flex-col items-end gap-1.5">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <span className="mono rounded-full bg-cos-teal-faint px-2.5 py-1 text-[12px] font-semibold text-cos-teal-deep">
                      {num(selectedCount)} ticked
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="select_all"
                      onClick={selectAllMatching}
                      disabled={loadingList || filtered.length === 0 || allMatchingSelected}
                      title={isFiltered ? `Tick every vendor the filters match, on every page (${num(filtered.length)})` : `Tick every vendor, on every page (${num(filtered.length)})`}
                    >
                      Select all {num(filtered.length)}
                    </Button>
                    <Button variant="ghost" size="sm" icon="deselect" onClick={clearSelection} disabled={selectedCount === 0}>
                      Clear
                    </Button>
                    <VendorSheetUpload onResolved={onSheet} disabled={loadingList} />
                  </div>
                  {/* The last sheet's outcome stays under the button until the next upload. */}
                  {sheet && (
                    <p className="max-w-[560px] text-right text-[12px] leading-[17px] text-cos-ink-secondary" title={sheet.missing.length ? `Not found: ${sheet.missing.join(', ')}` : undefined}>
                      <Icon name="upload_file" className="mr-1 align-[-3px] text-[15px] text-cos-teal-deep" />
                      <span className="mono font-semibold text-cos-ink">{sheet.file}</span>:{' '}
                      <span className={sheet.matched.length === 0 ? 'text-cos-status-coral' : sheet.missing.length ? 'text-cos-status-amber' : ''}>
                        {describeSheet(sheet, { maxCodes: 12 })}
                      </span>
                      {sheetHidden > 0 && (
                        <>
                          {' · '}
                          {num(sheetHidden)} of the matched are ticked but hidden by the filters —{' '}
                          <button type="button" className="font-semibold text-cos-teal-deep hover:underline" onClick={clearFilters}>
                            show all
                          </button>
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* The needs filter first: it is the question this step answers. */}
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-cos-teal/25 bg-cos-teal-faint/40 px-3 py-2">
                <Icon name="rule" className="text-[18px] text-cos-teal-deep" weight={500} />
                <span className="text-[12px] font-semibold text-cos-ink">Show</span>
                <FilterSelect
                  label="Needs"
                  icon="rule"
                  value={needLabel}
                  options={NEEDS_FILTERS.map((f) => f.label)}
                  onChange={(label) => {
                    setNeed(NEEDS_FILTERS.find((f) => f.label === label)?.key || 'all');
                    setPage(1);
                  }}
                />
                <span className="text-[12px] text-cos-ink-secondary">
                  {need === 'all'
                    ? 'Everyone. Narrow to the vendors a check has not reached yet, or whose record is missing an input.'
                    : need.startsWith('missing')
                      ? 'These records lack the input, so a run cannot verify it — ask the vendor for it through the other mode.'
                      : `${num(filtered.length)} vendor${filtered.length === 1 ? '' : 's'} match — a run on them fills exactly this gap.`}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search name, code or email…" className="min-w-[220px] flex-1" />
                {FILTER_FIELDS.map((f) => {
                  const values = optionValues(f);
                  const labelToValue = new Map(values.map((v) => [humanise(f.key, v), v]));
                  return (
                    <FilterSelect
                      key={f.key}
                      label={f.label}
                      icon={f.icon}
                      value={humanise(f.key, filters[f.key])}
                      options={['All', ...values.map((v) => humanise(f.key, v))]}
                      onChange={(label) => setFilter(f.key, label === 'All' ? 'All' : labelToValue.get(label) ?? label)}
                    />
                  );
                })}
                {(activeFilters > 0 || search) && (
                  <Button variant="ghost" size="sm" icon="filter_alt_off" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
              </div>

              {all.truncated && (
                <p className="flex items-center gap-1.5 text-[12px] text-cos-status-amber">
                  <Icon name="warning" className="text-[15px]" />
                  Only the first {num(all.loaded)} of {num(all.total)} vendors are loaded — use search or the Excel upload to reach the rest.
                </p>
              )}
              {/* How many there are, how many match, how many are ticked — and the way from "this page" to "everyone". */}
              {!loadingList && all.vendors && (
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[12px] text-cos-ink-secondary">
                  <span>
                    {isFiltered ? (
                      <>
                        <span className="mono font-semibold text-cos-ink">{num(filtered.length)}</span> of{' '}
                        <span className="mono font-semibold text-cos-ink">{num(base.length)}</span> vendors match these filters
                      </>
                    ) : (
                      <>
                        All <span className="mono font-semibold text-cos-ink">{num(base.length)}</span> vendors
                      </>
                    )}
                    {' · '}
                    <span className="mono font-semibold text-cos-teal-deep">{num(selectedCount)}</span> ticked
                  </span>
                  {pageAll && !allMatchingSelected && (
                    <button type="button" className="font-semibold text-cos-teal-deep hover:underline" onClick={selectAllMatching}>
                      Only the {num(vendors.length)} on this page are ticked — select all {num(filtered.length)}
                      {isFiltered ? ' matching' : ''}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[rgba(250,249,244,0.96)]">
                  <tr className="border-b border-cos-line">
                    <th className="w-10 px-4 py-2.5 text-left">
                      <PageCheckbox all={pageAll} some={pageSome} onChange={togglePage} disabled={vendors.length === 0} />
                    </th>
                    {['Vendor', 'MSME', 'State', 'Status', 'Verified so far', 'This run can verify'].map((h) => (
                      <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingList ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10">
                        <div className="flex flex-col items-center gap-2 text-cos-ink-secondary">
                          <Icon name="hourglass_top" className="text-[24px] text-cos-teal-deep" />
                          <span className="text-sm">Loading vendors… {all.total ? `${num(all.loaded)} of ${num(all.total)}` : ''}</span>
                        </div>
                      </td>
                    </tr>
                  ) : all.error && !all.vendors ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center">
                        <p className="text-[13px] text-cos-status-coral">{all.error}</p>
                        <Button variant="secondary" size="sm" icon="refresh" className="mt-3" onClick={all.reload}>Try again</Button>
                      </td>
                    </tr>
                  ) : vendors.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-14 text-center">
                        <div className="flex flex-col items-center gap-2 text-cos-ink-muted">
                          <Icon name="search_off" className="text-[28px]" />
                          <span className="text-sm">No vendors match these filters.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    vendors.map((v) => {
                      const ticked = selected.has(v.id);
                      const p = vendorPill(v.status);
                      const st = state.get(v.id) || { inputs: [], verified: new Set() };
                      return (
                        <tr
                          key={v.id}
                          onClick={() => toggle(v)}
                          className={`cursor-pointer border-b border-cos-line-subtle transition-colors last:border-0 ${ticked ? 'bg-[rgba(13,168,155,0.05)]' : 'hover:bg-white/50'}`}
                        >
                          <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" className={CHECKBOX} checked={ticked} onChange={() => toggle(v)} aria-label={`${ticked ? 'Untick' : 'Tick'} ${v.company_name}`} />
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-cos-ink">{v.company_name}</div>
                            <div className="mono text-2xs text-cos-ink-muted">{v.vendor_code || 'No vendor code'}</div>
                          </td>
                          <td className="px-3 py-2 text-cos-ink-secondary">{v.msme_status ? MSME_LABEL[v.msme_status] || v.msme_status : '—'}</td>
                          <td className="px-3 py-2 text-cos-ink-secondary">{v.registered_state || v.region || '—'}</td>
                          <td className="px-3 py-2">
                            <StatusPill status={p.pill} label={p.label} />
                          </td>
                          <td className="px-3 py-2">
                            <VerifiedChips verified={st.verified} />
                          </td>
                          <td className="px-3 py-2">
                            {checkKeys.length === 0 ? (
                              <span className="text-cos-ink-muted">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {checkKeys.map((k) => {
                                  const c = checkByKey(k);
                                  const ok = st.inputs.includes(c.needs);
                                  return (
                                    <span
                                      key={k}
                                      title={ok ? `${c.label}: ${c.needsLabel} present` : `${c.label}: no ${c.needsLabel} — will be skipped, not charged`}
                                      className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-2xs font-semibold ${
                                        ok ? `${toneFor(k).bg} ${toneFor(k).text}` : 'bg-[rgba(70,80,72,0.06)] text-cos-ink-muted line-through'
                                      }`}
                                    >
                                      <Icon name={ok ? 'check' : 'remove'} className="text-[12px]" weight={600} />
                                      {c.label}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-cos-line-subtle px-5 py-3">
              <Pagination page={safePage} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} noun="vendors" />
            </div>
          </GlassCard>
        )}

        {/* ------------------------------------------------------------------ 3 review */}
        {step === 2 && (
          <div className="flex flex-col gap-4">
            <GlassCard>
              <label className={LABEL} htmlFor="run-name">Run name <span className="font-normal text-cos-ink-muted">(optional)</span></label>
              <input id="run-name" className={FIELD} value={name} onChange={(e) => setName(e.target.value)} placeholder="Q3 bank re-check" maxLength={80} />
              <p className="mt-1 text-[12px] text-cos-ink-secondary">How this run is listed. Vendors never see it — they are not contacted at all.</p>
            </GlassCard>

            <GlassCard padded={false}>
              <div className="px-5 pt-5">
                <h3 className="text-[15px] font-bold tracking-tight text-cos-ink">What will run</h3>
                <p className="mt-0.5 text-[12px] text-cos-ink-secondary">One call per vendor per check. A vendor missing the input a check needs is skipped and not charged.</p>
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-cos-line">
                      {['Check', 'Vendors', 'Skipped', 'Calls', 'Cost'].map((h, i) => (
                        <th key={h} className={`whitespace-nowrap px-5 py-2.5 text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted ${i === 0 ? 'text-left' : 'text-right'}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(estimate?.per_check || []).map((e) => {
                      const c = checkByKey(e.key);
                      return (
                        <tr key={e.key} className="border-b border-cos-line-subtle last:border-0">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2 font-semibold text-cos-ink">
                              <Icon name={c.icon} className="text-[18px] text-cos-teal-deep" />
                              {c.label}
                            </div>
                            <div className="mono text-2xs text-cos-ink-muted">
                              {c.local ? 'Free · from the GSTIN, no call' : `${formatInr(e.price_inr ?? c.price_inr)} per vendor`}
                            </div>
                          </td>
                          <td className="mono px-5 py-3 text-right text-cos-ink">{num(e.runnable)}</td>
                          <td className={`mono px-5 py-3 text-right ${e.skipped ? 'text-cos-status-amber' : 'text-cos-ink-muted'}`} title={e.skipped_reason}>
                            {num(e.skipped)}
                          </td>
                          <td className="mono px-5 py-3 text-right text-cos-ink">{num(e.calls)}</td>
                          <td className="mono px-5 py-3 text-right font-semibold text-cos-ink">{c.local ? 'Free' : formatInr(e.cost_inr)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-cos-line bg-white/40">
                      <td className="px-5 py-3 text-[13px] font-bold text-cos-ink">
                        {num(selectedCount)} vendor{selectedCount === 1 ? '' : 's'}
                        {skippedTotal > 0 && <span className="ml-2 text-[12px] font-normal text-cos-ink-secondary">{num(skippedTotal)} check{skippedTotal === 1 ? '' : 's'} skipped for missing inputs</span>}
                      </td>
                      <td />
                      <td />
                      <td className="mono px-5 py-3 text-right font-bold text-cos-ink">{num(estimate?.calls || 0)}</td>
                      <td className="mono px-5 py-3 text-right text-[15px] font-bold text-cos-ink">{formatInr(estimate?.cost_inr || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </GlassCard>

            <GlassCard accentRail>
              <div className="flex flex-col gap-3">
                <p className="flex items-start gap-2 text-[13px] text-cos-ink">
                  <Icon name="unsubscribe" className="mt-px shrink-0 text-[18px] text-cos-teal-deep" weight={500} />
                  <span><span className="font-semibold">No email goes to any vendor.</span> This checks our records against the registries; nobody is contacted.</span>
                </p>
                <p className="flex items-start gap-2 text-[13px] text-cos-ink">
                  <Icon name="payments" className="mt-px shrink-0 text-[18px] text-cos-teal-deep" weight={500} />
                  <span>
                    <span className="font-semibold">{formatInr(estimate?.cost_inr || 0)} will be charged</span> to the verification account when you start
                    {usage.data?.data ? (
                      <span className="text-cos-ink-secondary"> — {num(usage.data.data.calls_this_month)} paid calls made this month so far.</span>
                    ) : '.'}
                  </span>
                </p>
                <p className="flex items-start gap-2 text-[13px] text-cos-ink">
                  <Icon name="task_alt" className="mt-px shrink-0 text-[18px] text-cos-teal-deep" weight={500} />
                  <span><span className="font-semibold">Nothing is written yet.</span> Results wait for your approval before any vendor record changes.</span>
                </p>
                <label className={`mt-1 flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[13px] ${isAdmin ? 'cursor-pointer border-cos-line bg-white/60' : 'cursor-not-allowed border-cos-line bg-white/30 text-cos-ink-muted'}`}>
                  <input type="checkbox" className={`${CHECKBOX} mt-0.5`} checked={ack} disabled={!isAdmin} onChange={(e) => setAck(e.target.checked)} />
                  <span>
                    I understand <span className="mono font-semibold">{formatInr(estimate?.cost_inr || 0)}</span> will be charged for{' '}
                    <span className="mono font-semibold">{num(estimate?.calls || 0)}</span> registry calls.
                    {!isAdmin && <span className="block text-[12px]">Only an admin can start a run.</span>}
                  </span>
                </label>
              </div>
            </GlassCard>
          </div>
        )}

        {/* footer — on the vendors step the list runs long, so the bar pins to the bottom of the viewport (the campaign wizard's bar) */}
        <div
          className={
            step === 1
              ? 'sticky bottom-0 z-10 flex items-center justify-between gap-3 rounded-2xl border border-cos-line bg-[rgba(250,249,244,0.94)] px-4 py-3 shadow-[0_-8px_24px_-14px_rgba(20,30,25,0.3)] backdrop-blur'
              : 'flex items-center justify-between gap-3 pt-1'
          }
        >
          <Button variant="ghost" icon="chevron_left" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || starting}>
            Back
          </Button>
          <div className="flex items-center gap-3">
            {step === 0 && checkKeys.length === 0 && <span className="text-[12px] text-cos-ink-muted">Pick at least one check</span>}
            {step === 1 && selectedCount === 0 && <span className="text-[12px] text-cos-ink-muted">Tick at least one vendor</span>}
            {step === 1 && selectedCount > 0 && (
              <span className="mono text-[12px] font-semibold text-cos-teal-deep">
                {num(selectedCount)} vendor{selectedCount === 1 ? '' : 's'} ticked
              </span>
            )}
            {step < 2 ? (
              <Button variant="primary" iconRight="chevron_right" onClick={() => setStep((s) => s + 1)} disabled={!canContinue}>
                Continue
              </Button>
            ) : (
              <Button variant="primary" icon={starting ? 'hourglass_top' : 'play_arrow'} onClick={start} disabled={!canStart}>
                {starting ? 'Starting…' : `Start verification · ${formatInr(estimate?.cost_inr || 0)}`}
              </Button>
            )}
          </div>
        </div>
      </div>

      <EstimateRail checks={checks} vendorCount={selectedCount} estimate={estimate} step={step} catalogue={catalogue} />
    </div>
  );
}

export default SelfRegularizationWizard;
